import React, { useState } from 'react';

interface PlanInfo {
  id: string;
  name: string;
  price_display: string;
  jobs: string;
  desc: string;
  features: string[];
  stripePriceId: string;
}

interface SubData {
  id: string;
  plan: string;
  plan_type: string;
  status: string;
  jobs_used: number;
  job_limit: number;
  is_unlimited: boolean;
  trial_end: string;
  has_stripe_customer: boolean;
  details: {
    name: string;
    price: string;
    jobs: string;
    desc: string;
    features: string[];
  };
}

const PLAN_ORDER = ['standard'];

const SubscriptionTab: React.FC<{ token: string }> = ({ token }) => {
  const [subscription, setSubscription] = useState<SubData | null>(null);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState('');
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [showPlans, setShowPlans] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDone, setDeleteDone] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDone, setCancelDone] = useState(false);

  React.useEffect(() => {
    loadSubscription();
    loadPlans();
  }, []);

  const loadSubscription = async () => {
    setSubLoading(true);
    setSubError('');
    try {
      const res = await fetch('/api/tenant/subscription', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSubscription(await res.json());
      else setSubError('Failed to load subscription');
    } catch (err) {
      setSubError('Error loading subscription');
    } finally {
      setSubLoading(false);
    }
  };

  const loadPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await fetch('/api/tenant/plans', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setPlans(await res.json());
    } catch (err) { /* ignore */ }
    setPlansLoading(false);
  };

  const handleManageBilling = async () => {
    try {
      const res = await fetch('/api/tenant/create-portal-session', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else alert('Failed to open billing portal');
    } catch (err) {
      alert('Error opening billing portal');
    }
  };

  const handleSubscribe = async (planType: string) => {
    setSubscribing(planType);
    try {
      const res = await fetch('/api/tenant/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan_type: planType })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else alert('No checkout URL returned');
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to start checkout');
      }
    } catch (err) {
      alert('Error starting checkout');
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const res = await fetch('/api/tenant/create-portal-session', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else { setCancelDone(true); setCancelConfirm(false); }
      } else {
        alert('No active billing to cancel. Contact admin.');
      }
    } catch (err) {
      alert('Error cancelling subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  // ... rest of the functions (export, delete etc.)
  const escapeCSV = (val: any) => {
    const s = val === null || val === undefined ? '' : String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };

  const downloadCSV = (rows: string[][], filename: string) => {
    const csv = rows.map(r => r.map(escapeCSV).join(',')).join('\n');
    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const res = await fetch('/api/account/export', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const dateParts = new Date().toLocaleDateString('en-GB').split('/');
        const dateStr = dateParts[0] + dateParts[1] + dateParts[2];
        const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '';

        const jobsHeader = ['Job #', 'Customer', 'Company', 'Vehicle Reg', 'Service', 'Issue', 'Price', 'Status', 'SERMI', 'Date Created', 'Date Completed', 'V5C Image', 'Photo ID', 'Signature Name', 'Signature Date'];
        const jobsRows = [jobsHeader];
        (data.jobs || []).forEach((job: any) => {
          jobsRows.push([job.job_number || '', job.customer_name || '', job.company_name || '', job.vehicle_reg || '', job.service_type || '', job.issue || '', job.price || '', job.status || '', job.is_sermi ? 'Yes' : 'No', fmtDate(job.created_at), fmtDate(job.completed_at), job.v5c_image_url || '', job.photo_id_image_url || '', job.signature_name || '', job.signature_date || '']);
        });
        downloadCSV(jobsRows, 'locktrak-jobs-' + dateStr + '.csv');

        const custHeader = ['Name', 'Email', 'Phone', 'Company', 'Address', 'Postcode', 'Date Created'];
        const custRows = [custHeader];
        (data.customers || []).forEach((c: any) => {
          custRows.push([(c.first_name || '') + ' ' + (c.surname || ''), c.email || '', c.mobile_tel || c.phone || '', c.company_name || '', c.address || '', c.postcode || '', c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '']);
        });
        downloadCSV(custRows, 'locktrak-customers-' + dateStr + '.csv');
        setExportDone(true);
        setTimeout(() => setExportDone(false), 3000);
      } else alert('Failed to export data');
    } catch (err) {
      alert('Error exporting data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const res = await fetch('/api/account/delete', { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
      if (res.ok) {
        setDeleteDone(true);
        setTimeout(() => { localStorage.removeItem('token'); window.location.reload(); }, 2000);
      } else alert('Failed to delete account');
    } catch (err) {
      alert('Error deleting account');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (subLoading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Loading subscription details...</div>;
  }

  if (subError) {
    return <div style={{ background: '#fee2e2', color: '#991b1b', padding: '16px', borderRadius: '12px', marginBottom: '16px', fontSize: '14px' }}>{subError}</div>;
  }

  if (!subscription) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>{'💳'}</div>
        <h3 style={{ color: '#0f172a', margin: '0 0 8px 0' }}>No Subscription Data</h3>
        <p style={{ color: '#94a3b8', margin: 0 }}>Subscription information will appear here once available.</p>
      </div>
    );
  }

  const jobsUsed = subscription.jobs_used || 0;
  const jobLimit = subscription.job_limit;
  const usagePercent = jobLimit > 0 ? Math.min(100, (jobsUsed / jobLimit) * 100) : 0;
  const isTrial = subscription.status === 'trial';
  const trialEndDate = subscription.trial_end ? new Date(subscription.trial_end) : null;
  const trialDaysRemaining = trialEndDate ? Math.max(0, Math.ceil((trialEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0;

  const planColor = (planType: string) => {
    const colors: any = {
      standard: { bg: '#eff6ff', color: '#1e40af' },
      trial: { bg: '#f1f5f9', color: '#64748b' }
    };
    return colors[planType] || colors.trial;
  };

  const planStyle = planColor(subscription.plan_type || 'trial');

  return (
    <div>
      {/* Plan & Usage Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'white', padding: '24px', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', borderLeft: '4px solid ' + planStyle.color }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Current Plan</div>
            <div style={{ width: '36px', height: '36px', background: planStyle.bg, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>{'📋'}</div>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', lineHeight: 1.2 }}>{subscription.details?.name || subscription.plan || 'Trial'}</div>
          <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
            {subscription.details?.price || 'Free'}
            {subscription.details?.jobs ? ' \u00B7 ' + subscription.details.jobs + ' jobs/mo' : ''}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
            <span style={{
              padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
              background: isTrial ? '#fef3c7' : subscription.status === 'active' ? '#d1fae5' : '#fee2e2',
              color: isTrial ? '#92400e' : subscription.status === 'active' ? '#065f46' : '#991b1b'
            }}>
              {isTrial ? 'Trial' : subscription.status === 'active' ? 'Active' : subscription.status || 'Inactive'}
            </span>
            {subscription.has_stripe_customer && (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Connected to Stripe</span>
            )}
          </div>
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#64748b' }}>Jobs This Month</div>
            <div style={{ width: '36px', height: '36px', background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>{'🔧'}</div>
          </div>
          <div style={{ fontSize: '32px', fontWeight: '800', color: '#0f172a', lineHeight: 1 }}>
            {jobsUsed}
            <span style={{ fontSize: '18px', fontWeight: '500', color: '#94a3b8' }}> / {subscription.is_unlimited ? '∞' : jobLimit}</span>
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
            {subscription.is_unlimited ? 'unlimited jobs' : (jobLimit - jobsUsed) + ' jobs remaining this month'}
          </div>
          {!subscription.is_unlimited && jobLimit > 0 && (
            <div style={{ marginTop: '12px', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: Math.min(100, usagePercent) + '%', background: 'linear-gradient(90deg, #10b981, #059669)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
            </div>
          )}
        </div>
      </div>

      {/* Plan Details */}
      {subscription.details?.features && subscription.details.features.length > 0 && (
        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>Plan Features</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {subscription.details.features.map((f: string, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#475569' }}>
                <span style={{ color: '#10b981' }}>{'✅'}</span> {f}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Actions */}
      <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>Billing</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowPlans(!showPlans)}
            style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 12px rgba(5,150,105,0.3)' }}>
            {showPlans ? 'Hide Plans' : 'View & Change Plan'}
          </button>
          {subscription.has_stripe_customer && (
            <button onClick={handleManageBilling}
              style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #1e3c72, #2a5298)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 12px rgba(30,60,114,0.3)' }}>
              Manage Billing
            </button>
          )}
        </div>
        {isTrial && (
          <p style={{ fontSize: '13px', color: '#94a3b8', margin: '12px 0 0 0' }}>
            Trial: {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining. Select a plan below to subscribe.
          </p>
        )}
        {subscription.status === 'trial_expired' && (
          <p style={{ fontSize: '13px', color: '#991b1b', margin: '12px 0 0 0', background: '#fee2e2', padding: '8px 12px', borderRadius: '8px' }}>
            Your trial has expired. Choose a plan to continue using LockTrak.
          </p>
        )}
      </div>

      {/* Plan Selection */}
      {showPlans && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
          {plans.sort((a, b) => PLAN_ORDER.indexOf(a.id) - PLAN_ORDER.indexOf(b.id)).map(plan => {
            const isCurrentPlan = plan.id === subscription.plan_type;
            return (
              <div key={plan.id} style={{
                background: 'white', borderRadius: '14px', padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: isCurrentPlan ? '2px solid ' + planStyle.color : '1px solid #e2e8f0',
                position: 'relative'
              }}>
                {isCurrentPlan && (
                  <div style={{ position: 'absolute', top: '12px', right: '12px', background: planStyle.bg, color: planStyle.color, padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                    Current
                  </div>
                )}
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>{plan.name}</div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#1e3c72', marginBottom: '4px' }}>{plan.price_display}<span style={{ fontSize: '14px', fontWeight: '400', color: '#94a3b8' }}>/mo</span></div>
                <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '12px' }}>{plan.jobs} jobs per month</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>{plan.desc}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#475569' }}>
                      <span style={{ color: '#10b981' }}>{'✅'}</span> {f}
                    </div>
                  ))}
                </div>
                {!isCurrentPlan && (
                  <button onClick={() => handleSubscribe(plan.id)} disabled={subscribing === plan.id}
                    style={{ width: '100%', padding: '12px', background: '#1e3c72', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                    {subscribing === plan.id ? 'Redirecting...' : 'Subscribe to ' + plan.name}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Account Management */}
      <div style={{ background: 'white', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>Account Settings</h3>
        <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 16px 0' }}>
          Export your data or manage your account. These actions are permanent.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button onClick={handleExportData} disabled={exportLoading}
            style={{ padding: '10px 20px', background: exportLoading ? '#e2e8f0' : '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: exportLoading ? '#94a3b8' : '#475569' }}>
            {exportLoading ? 'Exporting...' : exportDone ? 'Exported!' : 'Export My Data'}
          </button>

          {subscription.has_stripe_customer && !cancelDone && (
            <button onClick={() => setCancelConfirm(true)}
              style={{ padding: '10px 20px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#92400e' }}>
              Cancel Subscription
            </button>
          )}
          {cancelDone && <span style={{ fontSize: '13px', color: '#059669', padding: '10px 0' }}>Cancellation requested</span>}

          <button onClick={() => setDeleteConfirm(true)}
            style={{ padding: '10px 20px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#dc2626' }}>
            Delete Account
          </button>
        </div>

        {cancelConfirm && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#fffbeb', borderRadius: '12px', border: '1px solid #fde68a' }}>
            <p style={{ fontSize: '14px', color: '#92400e', margin: '0 0 12px 0', fontWeight: '500' }}>
              Are you sure you want to cancel your subscription?
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={handleCancelSubscription} disabled={cancelLoading}
                style={{ padding: '8px 18px', background: '#92400e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                {cancelLoading ? 'Processing...' : 'Yes, Cancel'}
              </button>
              <button onClick={() => setCancelConfirm(false)}
                style={{ padding: '8px 18px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}>
                Keep Subscription
              </button>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca' }}>
            {deleteDone ? (
              <p style={{ fontSize: '14px', color: '#059669', margin: 0, fontWeight: '500' }}>Account deleted. Redirecting...</p>
            ) : (
              <>
                <p style={{ fontSize: '14px', color: '#dc2626', margin: '0 0 12px 0', fontWeight: '500' }}>
                  This will permanently delete all your data. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={handleDeleteAccount} disabled={deleteLoading}
                    style={{ padding: '8px 18px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>
                    {deleteLoading ? 'Deleting...' : 'Permanently Delete Everything'}
                  </button>
                  <button onClick={() => setDeleteConfirm(false)}
                    style={{ padding: '8px 18px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}>
                    Keep My Account
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionTab;
